/* eslint-disable react/require-default-props */
import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import PropTypes from 'prop-types'
import Alert from '@apps/components/Alert'
import { getBySlug } from '@apps/utils/contentful'
import {
  getPreferredSimSelectionText,
  getSimSelectionPageContent,
  getSimSelectionSlugValue
} from '@apps/utils/simSelectionContent'
import PhoneCompatibility from '@apps/components/PhoneCompatibility'
import InlineInstructionMarkdown from '@apps/components/InlineInstructionMarkdown'
import RoundedButton from '@apps/components/RoundedButton'
import { Image, ProductFeature, Typography } from '@telus/koodo-ui-kit'

const PHONE_DETAIL_LABEL_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 16,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: '24px'
}
const PHONE_DETAIL_VALUE_TOKENS = {
  color: '#000000',
  fontSize: 16,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '24px'
}

const PhoneDetails = ({
  phoneImage,
  manufacturer,
  model,
  imei,
  eSimCompatible,
  onChoosePSimClick,
  lteCompatible = false,
  showLteCompatibility = true,
  showIncompatibleMessage = true,
  layout = 'default',
  userType = ''
}) => {
  const { pageContent } = useSelector((state) => state.common.content)
  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: language } = useContext(ApplicationContext)

  const haveSimPageContent = getSimSelectionPageContent(pageContent, language, userType)
  const shortTextFields = haveSimPageContent?.shortTextFields || []
  const longTextFields = haveSimPageContent?.longTextFields || []

  const pageTextContent = useMemo(() => {
    if (!shortTextFields) return {}
    return {
      eSim: getSimSelectionSlugValue(shortTextFields, 'eSIM', userType),
      lteNetwork: getSimSelectionSlugValue(shortTextFields, 'lteNetwork', userType),
      manufacturer: getSimSelectionSlugValue(shortTextFields, 'manufacturer', userType),
      model: getSimSelectionSlugValue(shortTextFields, 'model', userType),
      imei: getSimSelectionSlugValue(shortTextFields, 'imei', userType),
      eSimIncompatible: getBySlug(shortTextFields, 'eSimIncompatible')?.value,
      eSimNotCompatibleMessage:
        getPreferredSimSelectionText(
          longTextFields,
          shortTextFields,
          'eSimNotCompatibleMessage'
        ),
      getNewPhysicalSIM: getBySlug(shortTextFields, 'getNewPhysicalSIM')?.value
    }
  }, [shortTextFields, longTextFields, userType])

  const compatibilityContent = (
    <div className={layout === 'panel' ? 'space-y-3' : 'mb-6'}>
      <PhoneCompatibility
        feature={pageTextContent?.eSim}
        compatible={eSimCompatible}
        useKitIcon={layout === 'panel'}
      />
      {showLteCompatibility && (
        <PhoneCompatibility
          feature={pageTextContent?.lteNetwork}
          compatible={lteCompatible}
          useKitIcon={layout === 'panel'}
        />
      )}
    </div>
  )

  const renderDetailLabel = (value) => (
    <Typography
      variant={{ size: 16, bold: true, color: '#000000' }}
      tokens={PHONE_DETAIL_LABEL_TOKENS}
    >
      {value}
    </Typography>
  )

  const renderDetailValue = (value) => (
    <Typography
      variant={{ size: 16, color: '#000000' }}
      tokens={PHONE_DETAIL_VALUE_TOKENS}
    >
      {value}
    </Typography>
  )

  if (layout === 'panel') {
    return (
      <div className="w-full">
        <ProductFeature
          tokens={{
            backgroundColor: '#f2f2f2',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            paddingTop: 28,
            paddingBottom: 28,
            paddingLeft: 48,
            paddingRight: 48
          }}
          testId="imei-compatibility-result-card"
        >
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-center sm:gap-12">
            <div className="flex w-full justify-center sm:w-[150px] sm:justify-end">
              <Image
                alt="Phone"
                src={phoneImage}
                className="max-h-[230px] w-auto object-contain"
              />
            </div>
            <div className="w-full max-w-[360px] text-black">
              <div className="mb-6">{compatibilityContent}</div>
              <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-y-4 text-black">
                <dt>
                  {renderDetailLabel(pageTextContent.manufacturer)}
                </dt>
                <dd className="m-0 min-w-0 break-words">
                  {renderDetailValue(manufacturer)}
                </dd>
                <dt>
                  {renderDetailLabel(pageTextContent.model)}
                </dt>
                <dd className="m-0 min-w-0 break-words">
                  {renderDetailValue(model)}
                </dd>
                <dt>
                  {renderDetailLabel(pageTextContent.imei)}
                </dt>
                <dd className="m-0 min-w-0 break-words">
                  {renderDetailValue(imei)}
                </dd>
              </dl>
            </div>
          </div>
        </ProductFeature>
        {!eSimCompatible && showIncompatibleMessage && (
          <div className="mt-6">
            <Alert
              header={pageTextContent.eSimIncompatible}
              message={(
                <InlineInstructionMarkdown>
                  {pageTextContent?.eSimNotCompatibleMessage}
                </InlineInstructionMarkdown>
              )}
            />
            <div className="w-full">
              <RoundedButton onClick={onChoosePSimClick} label={pageTextContent?.getNewPhysicalSIM} />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full">
      <div className="w-full grid grid-cols-12 pb-6">
        <div className="col-start-1 col-end-12 sm:col-end-3 pl-6 flex flex-row justify-center">
          <Image alt="Phone" src={phoneImage} className="w-min min-w-min" />
        </div>
        <div className="col-start-1 col-end-13 sm:col-start-4 sm:col-end-10 flex flex-col justify-center">
          {compatibilityContent}
          <div className="grid grid-cols-2 grid-flow-row gap-3 h-24 w-72">
            <div>{renderDetailLabel(pageTextContent.manufacturer)}</div>
            <div>{renderDetailValue(manufacturer)}</div>
            <div>{renderDetailLabel(pageTextContent.model)}</div>
            <div>{renderDetailValue(model)}</div>
            <div>{renderDetailLabel(pageTextContent.imei)}</div>
            <div>{renderDetailValue(imei)}</div>
          </div>
        </div>
      </div>
      {!eSimCompatible && showIncompatibleMessage && (
        <div>
          <Alert
            header={pageTextContent.eSimIncompatible}
            message={(
              <InlineInstructionMarkdown>
                {pageTextContent?.eSimNotCompatibleMessage}
              </InlineInstructionMarkdown>
            )}
          />
          <div className="w-full">
            <RoundedButton onClick={onChoosePSimClick} label={pageTextContent?.getNewPhysicalSIM} />
          </div>
        </div>
      )}
    </div>
  )
}

PhoneDetails.propTypes = {
  eSimCompatible: PropTypes.bool.isRequired,
  manufacturer: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  imei: PropTypes.string.isRequired,
  phoneImage: PropTypes.string.isRequired,
  lteCompatible: PropTypes.bool,
  showLteCompatibility: PropTypes.bool,
  onChoosePSimClick: PropTypes.func,
  showIncompatibleMessage: PropTypes.bool,
  layout: PropTypes.oneOf(['default', 'panel']),
  userType: PropTypes.string
}

export default PhoneDetails
